// Checkout transparente da assinatura — corpo do SubscriptionModal (mockup
// docs/superpowers/specs/2026-10-28-checkout-transparente-assinatura-mp-mockup.html,
// telas 1 e 2). Esquerda: período, resumo do plano, totais e linha do ciclo.
// Direita: abas Cartão (Brick do MP) / Pix. Abaixo de 820px vira uma coluna.
//
// Dois modos (decididos pelo modal):
// - contratação: `POST /subscriptions` com o período escolhido;
// - fatura aberta de assinatura ACTIVE/PAST_DUE (`fatura` preenchida):
//   `POST /subscriptions/invoices/:id/pay`, sem escolha de período.
//
// Confirmação: Pix e cartão em análise/recorrente esperam o socket
// (`company-<id>-payment` / `company-<id>-subscription`, action CONCLUIDA);
// cartão aprovado na hora fecha direto.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import CropFreeIcon from "@material-ui/icons/CropFree";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import EventIcon from "@material-ui/icons/Event";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";

import { AuthContext } from "../../context/Auth/AuthContext";
import useSubscription from "../../hooks/useSubscription";
import toastError, { opcoesDeErro } from "../../errors/toastError";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";
import PeriodCards from "./PeriodCards";
import CardPanel from "./CardPanel";
import PixPanel, { AlreadyPaid } from "./PixPanel";
import {
  abasDisponiveis,
  formatBRL,
  maxParcelas,
  metodoPara,
  parcelaLabel,
  totalDoCiclo,
  valoresDaFatura,
} from "./checkoutRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    split: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1.15fr)",
      "@media (max-width:820px)": { gridTemplateColumns: "minmax(0,1fr)" },
    },
    col: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 14,
      minWidth: 0,
      "@media (max-width:600px)": { padding: 16 },
    },
    left: {
      borderRight: `1px solid ${border}`,
      "@media (max-width:820px)": { borderRight: "none", borderBottom: `1px solid ${border}` },
    },
    right: { backgroundColor: isLight ? theme.palette.aeros.surface2 : "#0E1119" },
    microLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    planCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${alpha("#A044FF", 0.35)}`,
      borderRadius: 14,
      padding: "16px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    planHead: { display: "flex", alignItems: "center", gap: 8 },
    planName: { fontSize: 14, fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" },
    planBadge: {
      marginLeft: "auto",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: alpha("#A044FF", 0.15),
      borderRadius: 6,
      padding: "2px 8px",
      flexShrink: 0,
    },
    limits: { display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8 },
    limitBox: {
      backgroundColor: isLight ? theme.palette.aeros.surface2 : "#0E1119",
      borderRadius: 10,
      padding: 10,
      textAlign: "center",
      minWidth: 0,
    },
    limitValue: {
      display: "block",
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.1,
      fontVariantNumeric: "tabular-nums",
    },
    limitLabel: { fontSize: 11, color: theme.palette.text.secondary },
    totals: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      padding: "0 4px",
    },
    row: { display: "flex", gap: 12 },
    rowValue: {
      marginLeft: "auto",
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums",
      textAlign: "right",
      flexShrink: 0,
    },
    discount: { color: stateText(theme, AEROS.success, "success") },
    totalRow: {
      display: "flex",
      paddingTop: 6,
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      fontWeight: 700,
      color: theme.palette.text.primary,
      fontSize: 14,
    },
    cycle: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "flex-start",
      gap: 5,
      padding: "0 4px",
      lineHeight: 1.5,
      "& .MuiSvgIcon-root": { fontSize: 14, marginTop: 2, flexShrink: 0 },
    },
    tabs: {
      display: "flex",
      gap: 4,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 10,
      padding: 4,
    },
    tab: {
      flex: 1,
      minHeight: 44,
      borderRadius: 7,
      border: "none",
      background: "none",
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      cursor: "pointer",
      touchAction: "manipulation",
      transition: "background-color 150ms ease-out, color 150ms ease-out",
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 1 },
      "&:disabled": { cursor: "not-allowed", opacity: 0.45 },
    },
    tabOn: { backgroundColor: alpha("#A044FF", 0.15), color: theme.palette.text.primary },
    pill: {
      fontSize: 10,
      fontWeight: 700,
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.12),
      border: `1px solid ${alpha(AEROS.success, 0.3)}`,
      borderRadius: 99,
      padding: "1px 7px",
    },
    notice: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(AEROS.cyan, 0.06),
      border: `1px solid ${alpha(AEROS.cyan, 0.2)}`,
      borderRadius: 10,
      padding: "9px 12px",
      lineHeight: 1.45,
      "& .MuiSvgIcon-root": {
        fontSize: 16,
        color: stateText(theme, AEROS.cyan, "info"),
        flexShrink: 0,
      },
    },
    fine: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      textAlign: "center",
      lineHeight: 1.5,
    },
    primary: {
      minHeight: 44,
      fontSize: 13.5,
    },
  };
});

const SubscriptionCheckout = ({ checkout, fatura, onPaid }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { user, socket } = useContext(AuthContext);
  const { create, payInvoice, reconcile } = useSubscription();

  const { plan, prices, pixDiscountPercent, publicKey, cycleStartsToday } = checkout;
  const kind = fatura?.kind || "CYCLE";
  const abas = abasDisponiveis({ publicKey });

  const periods = useMemo(() => {
    if (fatura) {
      if (kind === "UPGRADE") return [];
      return [fatura.period || checkout.subscription?.period || "MENSAL"];
    }
    // Só os períodos com preço: plano só anual não oferece o mensal.
    const comPreco = ["MENSAL", "ANUAL"].filter((p) => Number(prices?.[p]) > 0);
    return comPreco.length ? comPreco : ["MENSAL"];
  }, [fatura, kind, checkout.subscription, prices]);

  const [period, setPeriod] = useState(() => {
    const candidato = fatura?.period || checkout.subscription?.period || "MENSAL";
    // Plano sem preço anual: uma PENDING anual antiga não pode deixar o
    // card selecionado fora da lista.
    if (fatura || Number(prices?.[candidato]) > 0) return candidato;
    return Number(prices?.ANUAL) > 0 ? "ANUAL" : "MENSAL";
  });
  const [tab, setTab] = useState(() => abas.find((a) => !a.disabled).id);
  const cardDisabled = abas.some((a) => a.id === "card" && a.disabled);
  const [pix, setPix] = useState(null);
  const [paidInvoiceId, setPaidInvoiceId] = useState(fatura?.id || null);
  // "card": cobrança em análise; "recurring": preapproval criado, ativação
  // vem pelo webhook.
  const [pending, setPending] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [checking, setChecking] = useState(false);

  const method = metodoPara(period, tab, kind);
  const valores = fatura
    ? valoresDaFatura({ fatura, method, pixDiscountPercent })
    : totalDoCiclo({ prices, period, method, pixDiscountPercent });
  const parcelas = maxParcelas(period, kind);
  const semPreco = !(valores.total > 0);

  // Pagamento e assinatura emitem CONCLUIDA para o mesmo ciclo (e o cartão
  // aprovado já fecha antes do socket): conclui uma vez só.
  const doneRef = useRef(false);
  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    toast.success(i18n.t("subscription.checkout.paid"));
    onPaid();
  }, [onPaid]);

  // Escuta só enquanto há pagamento aguardando confirmação.
  const awaiting = Boolean(pix || pending);
  const companyId = user?.companyId;
  useEffect(() => {
    if (!awaiting || !socket || !companyId) return undefined;
    const onEvent = (data) => {
      if (data?.action === "CONCLUIDA") finish();
    };
    const paymentEvent = `company-${companyId}-payment`;
    const subscriptionEvent = `company-${companyId}-subscription`;
    socket.on(paymentEvent, onEvent);
    socket.on(subscriptionEvent, onEvent);
    return () => {
      socket.off(paymentEvent, onEvent);
      socket.off(subscriptionEvent, onEvent);
    };
  }, [awaiting, socket, companyId, finish]);

  const pay = (body) =>
    fatura ? payInvoice(fatura.id, body) : create({ period, ...body });

  const handlePeriod = (next) => {
    if (next === period) return;
    setPeriod(next);
    setPix(null);
    setPending(null);
  };

  const handleCardSubmit = async (formData) => {
    try {
      const data = await pay({
        method,
        cardToken: formData.token,
        installments: formData.installments,
        paymentMethodId: formData.payment_method_id,
        issuerId: formData.issuer_id,
        payer: formData.payer,
      });
      setPix(null);
      if (data?.invoice?.id) setPaidInvoiceId(data.invoice.id);
      if (data?.charge?.status === "approved") {
        finish();
      } else {
        setPending(data?.charge ? "card" : "recurring");
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(i18n.t("subscription.checkout.cardRejected"), opcoesDeErro);
      } else {
        toastError(err);
      }
      // Rejeitar devolve o Brick ao estado editável.
      throw err;
    }
  };

  const handleGeneratePix = async () => {
    setGenerating(true);
    try {
      const data = await pay({ method: "PIX" });
      setPending(null);
      setPix(data?.pix || null);
      if (data?.invoice?.id) setPaidInvoiceId(data.invoice.id);
    } catch (err) {
      toastError(err);
    } finally {
      setGenerating(false);
    }
  };

  // Não relê só o banco: o backend consulta o Mercado Pago e aplica o
  // pagamento se o webhook se perdeu (ou ainda não chegou).
  const handleAlreadyPaid = async () => {
    setChecking(true);
    try {
      const data = await reconcile();
      const quitada = !data?.openInvoice || data.openInvoice.id !== paidInvoiceId;
      if (data?.subscription?.status === "ACTIVE" && quitada) {
        finish();
      } else {
        toast.info(i18n.t("subscription.checkout.stillPending"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setChecking(false);
    }
  };

  const periodLine = (() => {
    if (kind === "UPGRADE" || !plan?.name) return plan?.name || "";
    return i18n.t(
      period === "ANUAL"
        ? "subscription.checkout.lineYearly"
        : "subscription.checkout.lineMonthly",
      { plan: plan.name }
    );
  })();

  const cycleLine = (() => {
    if (fatura) return null;
    const next = moment(cycleStartsToday)
      .add(1, period === "ANUAL" ? "year" : "month")
      .format("DD/MM/YYYY");
    if (tab === "pix") return i18n.t("subscription.checkout.cyclePix", { date: next });
    return i18n.t(
      period === "ANUAL"
        ? "subscription.checkout.cycleCardYearly"
        : "subscription.checkout.cycleCardMonthly",
      { date: next }
    );
  })();

  const totalLabel = formatBRL(valores.total);
  const submitLabel = i18n.t(
    fatura ? "subscription.checkout.payInvoice" : "subscription.checkout.subscribe",
    { value: totalLabel }
  );

  const limits = [
    { value: plan?.users, label: i18n.t("subscription.checkout.users") },
    { value: plan?.connections, label: i18n.t("subscription.checkout.connections") },
    { value: plan?.queues, label: i18n.t("subscription.checkout.queues") },
  ];

  const tabMeta = {
    card: { label: i18n.t("subscription.checkout.tabCard"), Icon: CreditCardIcon },
    pix: { label: i18n.t("subscription.checkout.tabPix"), Icon: CropFreeIcon },
  };

  const waitingText =
    pending === "card"
      ? i18n.t("subscription.checkout.waitingCard")
      : i18n.t("subscription.checkout.waitingRecurring");

  return (
    <div className={classes.split}>
      <div className={`${classes.col} ${classes.left}`}>
        {periods.length > 0 && (
          <>
            <div className={classes.microLabel}>
              {i18n.t(
                fatura ? "subscription.checkout.invoicePeriod" : "subscription.checkout.period"
              )}
            </div>
            <PeriodCards
              prices={prices}
              period={period}
              periods={periods}
              onChange={handlePeriod}
              cycleStartsToday={cycleStartsToday}
              disabled={Boolean(fatura) || Boolean(pending)}
            />
          </>
        )}

        <div className={classes.microLabel}>
          {i18n.t("subscription.checkout.planSummary")}
        </div>
        <div className={classes.planCard}>
          <div className={classes.planHead}>
            <span className={classes.planName}>{plan?.name || "—"}</span>
            <span className={classes.planBadge}>
              {i18n.t("subscription.checkout.selected")}
            </span>
          </div>
          <div className={classes.limits}>
            {limits.map((limit) => (
              <div key={limit.label} className={classes.limitBox}>
                <b className={classes.limitValue}>{limit.value ?? "—"}</b>
                <span className={classes.limitLabel}>{limit.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.totals}>
          <div className={classes.row}>
            <span>{periodLine}</span>
            <span className={classes.rowValue}>{formatBRL(valores.bruto)}</span>
          </div>
          {tab === "card" && parcelas > 1 && (
            <div className={classes.row}>
              <span>{i18n.t("subscription.checkout.installmentsRow")}</span>
              <span className={classes.rowValue}>
                {i18n.t("subscription.checkout.installmentsUpTo", {
                  label: parcelaLabel(valores.total, parcelas),
                })}
              </span>
            </div>
          )}
          {valores.desconto > 0 && (
            <div className={classes.row}>
              <span>
                {i18n.t("subscription.checkout.pixDiscountRow", {
                  percent: pixDiscountPercent,
                })}
              </span>
              <span className={`${classes.rowValue} ${classes.discount}`}>
                − {formatBRL(valores.desconto)}
              </span>
            </div>
          )}
          <div className={classes.totalRow}>
            <span>
              {i18n.t(
                fatura ? "subscription.checkout.totalInvoice" : "subscription.checkout.total"
              )}
            </span>
            <span className={classes.rowValue}>{totalLabel}</span>
          </div>
        </div>

        {cycleLine && (
          <div className={classes.cycle}>
            <EventIcon aria-hidden="true" />
            <span>{cycleLine}</span>
          </div>
        )}
      </div>

      <div className={`${classes.col} ${classes.right}`}>
        <div
          className={classes.tabs}
          role="tablist"
          aria-label={i18n.t("subscription.checkout.tabsLabel")}
        >
          {abas.map(({ id: key, disabled: tabDisabled }) => {
            const { label, Icon } = tabMeta[key];
            const on = key === tab;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={on}
                disabled={Boolean(pending) || tabDisabled}
                className={`${classes.tab} ${on ? classes.tabOn : ""}`}
                onClick={() => setTab(key)}
              >
                <Icon aria-hidden="true" />
                {label}
                {key === "pix" && Number(pixDiscountPercent) > 0 && (
                  <span className={classes.pill}>-{pixDiscountPercent}%</span>
                )}
              </button>
            );
          })}
        </div>

        {cardDisabled && !pending && (
          <div className={classes.notice} role="note">
            <CreditCardIcon />
            {i18n.t("subscription.checkout.cardUnavailable")}
          </div>
        )}

        {semPreco ? (
          <div className={classes.notice} role="alert">
            {i18n.t("subscription.checkout.noPrice")}
          </div>
        ) : pending ? (
          <>
            <div className={classes.notice} role="status" aria-live="polite">
              <HourglassEmptyIcon />
              {waitingText}
            </div>
            <AlreadyPaid onClick={handleAlreadyPaid} checking={checking} />
          </>
        ) : tab === "card" ? (
          <>
            <div className={classes.notice}>
              <LockOutlinedIcon />
              {i18n.t("subscription.checkout.secure")}
            </div>
            <CardPanel
              publicKey={publicKey}
              amount={valores.total}
              maxInstallments={parcelas}
              submitLabel={submitLabel}
              onSubmit={handleCardSubmit}
            />
            {method === "CARD_RECURRING" && !fatura && (
              <div className={classes.fine}>{i18n.t("subscription.checkout.terms")}</div>
            )}
          </>
        ) : (
          <PixPanel
            pix={pix}
            submitLabel={i18n.t("subscription.checkout.generatePix", { value: totalLabel })}
            submitClassName={`${m.submitButton} ${classes.primary}`}
            generating={generating}
            onGenerate={handleGeneratePix}
            alreadyPaid={<AlreadyPaid onClick={handleAlreadyPaid} checking={checking} />}
          />
        )}
      </div>
    </div>
  );
};

export default SubscriptionCheckout;
